import {
  type ArithmeticProblem,
  formatAnswer,
  formatNumber,
  OPERATION_SYMBOL,
} from "@/lib/tool-kit/worksheets/arithmetic";
import type { ProblemLayout } from "@/lib/tool-kit/worksheets/layout";

const LINE = "border-neutral-900";

function Blank({ width = "3ch" }: { width?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block border-neutral-900 border-b align-baseline ${LINE}`}
      style={{ minWidth: width }}
    >
      &nbsp;
    </span>
  );
}

/** `a × b = ____`, or with a factor left blank. */
function OneLine({
  problem,
  remainderLabel,
  withRemainder,
}: {
  problem: ArithmeticProblem;
  remainderLabel: string;
  withRemainder: boolean;
}) {
  const symbol = OPERATION_SYMBOL[problem.op];
  const parts = problem.terms.map((term, index) =>
    (problem.blank === "first" && index === 0) ||
    (problem.blank === "second" && index === 1) ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: terms are positional
      <Blank key={index} />
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: terms are positional
      <span key={index}>{term}</span>
    )
  );
  const total =
    problem.blank === "answer" ? (
      <>
        <Blank width={withRemainder ? "2.5ch" : "4ch"} />
        {withRemainder ? (
          <>
            {" "}
            {remainderLabel} <Blank width="2ch" />
          </>
        ) : null}
      </>
    ) : (
      <span>{formatNumber(problem.answer)}</span>
    );
  return (
    <div className="whitespace-nowrap font-mono tabular-nums leading-[2.2]">
      {parts.flatMap((part, index) =>
        index === 0 ? [part] : [` ${symbol} `, part]
      )}{" "}
      = {total}
    </div>
  );
}

/** The classic stacked column: terms right-aligned, the operator beside the last. */
function Stacked({
  problem,
  workRows,
  scaffoldBoxes,
}: {
  problem: ArithmeticProblem;
  workRows: number;
  scaffoldBoxes: number;
}) {
  const symbol = OPERATION_SYMBOL[problem.op];
  const width = Math.max(...problem.terms.map((t) => String(t).length)) + 2;
  const last = problem.terms.length - 1;
  return (
    <div
      className="font-mono tabular-nums leading-[1.35]"
      style={{ width: `${width}ch` }}
    >
      {scaffoldBoxes > 0 ? (
        <div aria-hidden="true" className="flex justify-end">
          {Array.from({ length: scaffoldBoxes }, (_, box) => (
            <span
              className="mx-[0.1ch] inline-block h-[0.75em] w-[0.8ch] border border-neutral-500 border-dotted"
              // biome-ignore lint/suspicious/noArrayIndexKey: identical decorative boxes
              key={box}
            />
          ))}
        </div>
      ) : null}
      {problem.terms.map((term, index) => (
        <div
          className={`flex justify-between ${index === last ? `border-b-2 ${LINE}` : ""}`}
          // biome-ignore lint/suspicious/noArrayIndexKey: terms are positional
          key={index}
        >
          <span>{index === last ? symbol : ""}</span>
          <span>{term}</span>
        </div>
      ))}
      {Array.from({ length: workRows }, (_, row) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: spacer rows
        <div key={row}>&nbsp;</div>
      ))}
      <div>&nbsp;</div>
      <div>&nbsp;</div>
    </div>
  );
}

/** Long-division bracket: divisor, then the dividend under an overline. */
function Bracket({
  problem,
  workRows,
}: {
  problem: ArithmeticProblem;
  workRows: number;
}) {
  const [dividend, divisor] = problem.terms;
  return (
    <div className="font-mono tabular-nums leading-[1.5]">
      <div>&nbsp;</div>
      <div className="flex items-end">
        <span className="pr-1">{divisor}</span>
        <span className={`border-t-2 border-l-2 pr-4 pl-2 ${LINE}`}>
          {dividend}
        </span>
      </div>
      {Array.from({ length: workRows }, (_, row) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: spacer rows
        <div key={row}>&nbsp;</div>
      ))}
    </div>
  );
}

export function ArithmeticProblemView({
  problem,
  layout,
  workRows,
  remainderLabel,
  withRemainder = false,
  scaffoldBoxes = 0,
}: {
  problem: ArithmeticProblem;
  layout: ProblemLayout;
  /** Blank lines left under a stacked problem for working. */
  workRows: number;
  remainderLabel: string;
  /** One-line division with a remainder blank after the quotient. */
  withRemainder?: boolean;
  /** Dotted boxes above a stacked problem for carried or regrouped digits. */
  scaffoldBoxes?: number;
}) {
  if (layout === "horizontal") {
    return (
      <OneLine
        problem={problem}
        remainderLabel={remainderLabel}
        withRemainder={withRemainder}
      />
    );
  }
  return problem.op === "divide" ? (
    <Bracket problem={problem} workRows={workRows} />
  ) : (
    <Stacked
      problem={problem}
      scaffoldBoxes={scaffoldBoxes}
      workRows={workRows}
    />
  );
}

/** `87 ÷ 7 = 12 R 3`: the answer-key line for a problem. */
export function equationText(
  problem: ArithmeticProblem,
  remainderLabel: string
): string {
  const symbol = OPERATION_SYMBOL[problem.op];
  return `${problem.terms.join(` ${symbol} `)} = ${formatAnswer(problem, remainderLabel)}`;
}
